//-tech-debt-react-query;
import { useQuery, useMutation, UseQueryOptions } from "@tanstack/react-query";
import {
  useFetchItems,
  salesforceId
} from "./base-hooks";
import { useRefetchEscalationRequests } from "./escalation-request-hooks";
import { CacheKey } from "../types/cache-keys";
import {
  ApprovalRequest,
  ApprovalRequestPayload,
  ApprovalRequestSource,
  ApprovalRequestType,
  RequestHistoryPayload
} from "../types/approval-request";
import { fetchItems, postItem, putItem } from "../support/queries";
import { getApprovalRequestDataViaSource } from "../support/approvalTemplateSources";
import { queryClient } from '../support/queryClient';
import { createCacheKeyForApprovalRequest } from "../support/approvalsHelpers";


// Hook to fetch approvals for list view
const useFetchApprovalRequests = (params: {[key: string]: string} = {}, options = {}) => {
  return useQuery<ApprovalRequest[]>({
    queryKey: [CacheKey.ApprovalRequest, 'list', params.status ?? 'All'],
    queryFn: async () => {
      const items = await fetchItems(`db/approval-request`, { ...params }, options);
      const escalationTypeApprovals = items.filter(
        (item) => item.requested_item_type === ApprovalRequestType.Escalation
      );

      const escalationPairs = await Promise.all(
        escalationTypeApprovals.map(async (item) => {
          const rows = await fetchItems(
            'db/escalation-approval',
            { approval_request: item.id, detailed: true },
            options
          );

          return {
            approvalRequestId: item.id,
            escalationId: rows?.[0]?.escalation?.id ?? null
          }
        })
      );

      const escalations = escalationPairs.reduce((acc, pair) => {
        if (pair.escalationId) {
          acc[pair.approvalRequestId] = pair.escalationId;
        }
        return acc;
      }, {} as Record<number, number>);

      return items.map((item) => {
        return { 
          ...item,
          requested_item: {
            [ApprovalRequestSource.Opportunity]: item.requested_item[ApprovalRequestSource.Opportunity]?.id ?? undefined,
            [ApprovalRequestSource.OpportunityProduct]: item.requested_item[ApprovalRequestSource.OpportunityProduct] ?? undefined,
            [ApprovalRequestSource.PoV]: item.requested_item[ApprovalRequestSource.PoV] ?? undefined,
            requested_item_type: item.requested_item_type,
            escalation: item.requested_item_type === ApprovalRequestType.Escalation ? escalations[item.id] ?? undefined : undefined,
          }
        }
      });
    },
  });
}

// Hook to fetch approval request details via template
const useFetchApprovalDetails = (approvalType, templateKeys, rootObjectIds, options) => {
  const query = useQuery({
    queryKey: [CacheKey.ApprovalRequestDetails, approvalType, ...Object.values(rootObjectIds ?? {})].map(String),
    queryFn: () => getApprovalRequestDataViaSource(approvalType, templateKeys ?? [], rootObjectIds),
    ...{ enabled: options?.enabled && !!approvalType && !!templateKeys && !!rootObjectIds },
  });

  return {
    ...query,
    data: query.data?.sourceData,
    failedSources: query.data?.failedSources ?? [],
  };
}

// Hooks for individual approval request
// Note: not using generic caching stategy to store approval requests due to needing to use request objects to find approval requests, and then
// having to load all requests to find the most recent. We want to avoid refetching all approval requests on every update. 


type ApprovalRequestItem = Pick<ApprovalRequest, 'id'>;

const useFetchApprovalRequestItems = (
  itemType: ApprovalRequestType,
  requestObjects: Record<string, number | string>,
  options?: Omit<
    UseQueryOptions<ApprovalRequestItem[], Error, ApprovalRequestItem['id'][]>,
    'queryKey' | 'queryFn' | 'select'
  >
) => {
  return useQuery<ApprovalRequestItem[], Error, ApprovalRequestItem['id'][]>({
    queryKey: [CacheKey.ApprovalRequestItem, itemType, ...Object.values(requestObjects).map(String)],
    queryFn: () => fetchItems('db/approval-request-item', { ...requestObjects }),
    ...{
        ...options,
        select: (data) => data?.map(item => item.id) ?? []
      },
  });
};

const useFetchApprovalRequestByObjectIds = (
  itemType: ApprovalRequestType,
  requestObjects: Record<string, number | string>,
  options?
) => {
  const { data: ids = [], isLoading: isLoadingIds } = useFetchApprovalRequestItems(itemType, requestObjects, options);

  const mainQueryKey = createCacheKeyForApprovalRequest(itemType, requestObjects);
  
  const { data, isLoading: isLoadingRequests } = useQuery({
    queryKey: mainQueryKey,
    queryFn: async () => {
      const requests = await Promise.all(
        ids.map(async (id) => {
          const arr = await fetchItems('db/approval-request', {
            requested_item_type: itemType,
            requested_item_id: id,
            detailed: true
          });
          return (arr && arr.length > 0) ? arr[0] : null;
        })
      );
      
      const validRequest = requests
        .filter(Boolean)
        .sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime())
      
      return validRequest?.[0] ?? null;
    },
    ...{
        enabled: ids.length > 0 && (options?.enabled ?? true),
        staleTime: 5 * 60 * 1000, // Won't auto-refetch for 5 min
        refetchOnWindowFocus: false // Disable focus refetch
      },
  });

  return {
    data,
    isLoading: isLoadingIds || isLoadingRequests,
    isFetched: (!isLoadingIds && ids.length === 0) || (!isLoadingRequests && ids.length > 0),
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.ApprovalRequestItem, itemType, ...Object.values(requestObjects).map(String)] });
      queryClient.invalidateQueries({ queryKey: mainQueryKey });
    }
  };
}

const useCreateApprovalRequestMutation = (requestObjects: Record<string, number | string>) => {
  return useMutation<ApprovalRequest, unknown, ApprovalRequestPayload>({
    mutationFn: (payload: ApprovalRequestPayload) => postItem('db/approval-request', payload),
    onSuccess: (_response, _variables, _context) => {
      if (_response?.requested_item_type) {
        const mainQueryKey = createCacheKeyForApprovalRequest(
          _response.requested_item_type,
          requestObjects
        );

        queryClient.setQueryData<ApprovalRequest>(mainQueryKey, () => _response);
        queryClient.invalidateQueries({
          queryKey: [CacheKey.ApprovalRequest, 'list'].map(String),
          refetchType: 'all',
        });
      }
    },
  });
}

const useUpdateApprovalRequestMutation = (requestObjects: Record<string, number | string>) => {
  const refetchEscalationRequests = useRefetchEscalationRequests(requestObjects[ApprovalRequestSource.Opportunity] as string, {}, { enabled: salesforceId(requestObjects[ApprovalRequestSource.Opportunity]) });

  return useMutation<
    ApprovalRequest,
    unknown,
    Partial<ApprovalRequestPayload> & { id: number }
  >({
    mutationFn: (payload) => putItem(`db/approval-request`, payload),
    onSuccess: async (_response) => {
      if (_response?.requested_item_type) {
        const mainQueryKey = createCacheKeyForApprovalRequest(
          _response.requested_item_type,
          requestObjects
        );

        // Update the single approval in cache
        queryClient.setQueryData<ApprovalRequest>(mainQueryKey, _response);
      }

      // Invalidate list for admin views
      queryClient.invalidateQueries({
        queryKey: [CacheKey.ApprovalRequest, 'list'],
        refetchType: 'all',
      });

      // Handle escalation-specific logic
      if (
        _response?.requested_item_type === ApprovalRequestType.Escalation &&
        requestObjects?.escalation
      ) {
        queryClient.invalidateQueries({
          queryKey: [
            CacheKey.EscalationRequest,
            requestObjects[ApprovalRequestSource.Opportunity],
            requestObjects.escalation,
          ].map(String),
          refetchType: 'all',
        });

        await refetchEscalationRequests();
      }
    },
  });
}

const useCreateEscalationApprovalMutation = (opportunityId: string) => {
  const refetchEscalationRequests = useRefetchEscalationRequests(opportunityId);

  return useMutation({
    mutationFn: (payload: any) => postItem('db/escalation-approval', payload),
    onSuccess: async (_response) => {
      queryClient.invalidateQueries({
        queryKey: [CacheKey.ApprovalRequest, 'list'],
        refetchType: 'all',
      });

      if (salesforceId(opportunityId)) {
        await refetchEscalationRequests();
      }
    },
  });
};

// History Hooks
const useFetchApprovalHistory = (requestId: number, enabled: boolean) => useFetchItems<any>(
  'db/approval-request-history',
  () => [CacheKey.ApprovalRequestHistory, requestId],
  {approval_request: requestId, detailed: true},
  {enabled}
);

const useCreateApprovalHistoryMutation = () => {
  return useMutation({
    mutationFn: (payload: RequestHistoryPayload) => postItem('db/approval-request-history', payload),
    onSettled: (_response, _error, _variables, _context) => {
      if (_response?.id) {
        queryClient.invalidateQueries({
          queryKey: [CacheKey.ApprovalRequestHistory, String(_variables?.approval_request_id)],
          refetchType: 'all',
        });
      }
    },
  });
};

export {
  useFetchApprovalRequestByObjectIds,
  useFetchApprovalRequests,
  useCreateApprovalRequestMutation,
  useUpdateApprovalRequestMutation,
  useFetchApprovalDetails,
  useFetchApprovalHistory,
  useCreateApprovalHistoryMutation,
  useCreateEscalationApprovalMutation
}